import React, { Fragment } from 'react';
import { useImmer } from 'use-immer';
import lodash from 'lodash';
import styles from './AssignRoles.scss';
import MultiSelectCheckbox from 'components/common/MultiSelectCheckbox/MultiSelectCheckbox';
import { DismissibleHeader } from 'components/common/DismissiblePanel';
import LabelWithTooltip from 'components/common/Tooltip/LabelWithTooltip';
import { formatDataForSelectWithRoles } from 'pages/studyOverview/utils/helperFunctions';
import ActionRow from '../components/ActionRow';
import { DismissButton } from 'components/common/DismissiblePanel';
import {
    SelectedOptionList,
    SelectedOptionCard,
    SelectedOptionLabels,
    SelectedOptionLeftColumn,
    SelectedOptionRightColumn,
} from 'components/common/SelectedOptionList';

export default function AssignRoles({
    availableRoles,
    documentType,
    onAssignRoles,
    assignedRoles,
    initiallyAssignedRoles,
    onCancel
}) {

    const [isDirty, setDirty] = useImmer(false);
    const [roles, setRoles] = useImmer(assignedRoles);

    const removeRole = (option) => {
        setRoles(currentRoles =>
            currentRoles.filter(role => role.id !== option.value)
        );
        setDirty(state => true);
    };

    const addRole = (selectedOptions) => {
        const result = lodash.intersectionWith(
            availableRoles,
            selectedOptions,
            (availableRole, option) => availableRole.id == option.value
        );
        const newResult = lodash.unionWith(result, initiallyAssignedRoles, lodash.isEqual);
        setRoles(state => newResult);
        setDirty(state => !lodash.isEmpty(newResult));
    };

    const cancelAssignRoles = () => {
        setRoles(state => assignedRoles);
        onCancel();
    };

    return (
        <Fragment data-cy="AssignRoles">
            <DismissibleHeader
                title='Assign Roles'
                subTitle={documentType.displayName}
            />
            <div className={styles.assignRolesWrapper} data-cy="AssignRoles-div">
                <div className={styles.assignRoles} data-cy="AssignRoles-divs">
                    <LabelWithTooltip
                        message='These roles are populated from the roles selected in the study creation workflow.'
                    >
                        Select Roles
                    </LabelWithTooltip>
                    <MultiSelectCheckbox
                        onChanged={addRole} 
                        options={formatDataForSelectWithRoles(availableRoles, 'name', 'id', 'isInitiallyAssigned')}
                        selectedOptions={formatDataForSelectWithRoles(roles, 'name', 'id', 'isInitiallyAssigned')}
                        placeholder={'Filter roles by name'}
                    />
                    <AssignedRolesList 
                        selectedOptions={formatDataForSelectWithRoles(roles, 'name', 'id', 'isInitiallyAssigned')}
                        onRemove={removeRole}
                    />
                </div>
                <ActionRow
                    primaryActionLabel={isDirty && lodash.isEmpty(roles) ? 'Remove Roles' : 'Assign Roles'}
                    onPrimaryAction={() => onAssignRoles(roles)}
                    onCancel={cancelAssignRoles}
                    primaryActionDisabled={!isDirty}
                />
            </div>
        </Fragment>
    );
}

export const AssignedRolesList = ( {
    selectedOptions,
    onRemove,
}) => {
    const renderCard = (option, index) => (
        <SelectedOptionCard key={index}>
            <SelectedOptionLeftColumn>
                <SelectedOptionLabels
                    withEllipsis={false}
                    isDisabled={option.isDisabled}
                    primaryLabel={option.label}
                />
            </SelectedOptionLeftColumn>
            <SelectedOptionRightColumn>
                {option.isDisabled ? null: <DismissButton onClick = {() => onRemove(option)} /> }
            </SelectedOptionRightColumn>
        </SelectedOptionCard>
    );
    return (
        <SelectedOptionList>
            {
                selectedOptions.map(renderCard)
            }
        </SelectedOptionList>
    );
};
